// Phone number validation and formatting utilities
// Backend pattern: ^\+?[1-9]\d{1,14}$
// This means: optional + sign, followed by a digit 1-9, then 1-14 more digits (total 2-15 digits)

export const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters except + at the beginning
  let formatted = value.replace(/[^\d+]/g, '');

  // Ensure + is only at the beginning
  if (formatted.includes('+') && formatted.indexOf('+') > 0) {
    formatted = formatted.replace(/\+/g, '');
  }

  // Remove leading zeros after + or at the start
  if (formatted.startsWith('+0')) {
    formatted = '+' + formatted.substring(2);
  } else if (formatted.startsWith('0')) {
    formatted = formatted.substring(1);
  }

  // Limit to 15 digits (not counting the +)
  const plusSign = formatted.startsWith('+') ? '+' : '';
  const digits = formatted.replace(/\+/g, '');

  if (digits.length > 15) {
    formatted = plusSign + digits.substring(0, 15);
  } else {
    formatted = plusSign + digits;
  }

  return formatted;
};

export const validatePhoneNumber = (phone: string): boolean => {
  return PHONE_REGEX.test(phone);
};

export const getPhoneNumberError = (phone: string): string | null => {
  if (!phone) {
    return 'Phone number is required';
  }

  const formatted = formatPhoneNumber(phone);

  if (formatted.length < 2) {
    return 'Phone number must have at least 2 digits';
  }

  if (formatted.length > 16) { // +15 digits maximum
    return 'Phone number must not exceed 15 digits';
  }

  if (!validatePhoneNumber(formatted)) {
    if (formatted.startsWith('+0') || formatted.startsWith('0')) {
      return 'Phone number cannot start with 0';
    }
    return 'Invalid phone number format. Use format: +1234567890 or 1234567890';
  }

  return null;
};

// Helper function to add country code if missing
export const addCountryCodeIfMissing = (phone: string, defaultCountryCode: string = '+263'): string => {
  const formatted = formatPhoneNumber(phone);

  // If no + sign and looks like a local number, add default country code
  if (!formatted.startsWith('+') && formatted.length >= 9 && formatted.length <= 10) {
    // For Zimbabwe, remove leading 0 if present for local numbers
    const localNumber = formatted.startsWith('0') ? formatted.substring(1) : formatted;
    return defaultCountryCode + localNumber;
  }

  return formatted;
};